/**
 * Goals service layer.
 * Abstracts all Firestore operations for user fitness goals.
 * Goals are stored as subcollections under user documents.
 */
import {
  collection,
  doc,
  setDoc,
  getDoc,
  getDocs,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  where,
  serverTimestamp,
  Timestamp,
} from 'firebase/firestore';
import { db } from './firebase';
import { handleServiceError } from '../utils/error-handler.util';

const USERS_COLLECTION = 'users';
const GOALS_SUBCOLLECTION = 'goals';

/**
 * Serialize a Firestore goal document snapshot into a plain object.
 * @param {import('firebase/firestore').DocumentSnapshot} docSnap
 * @returns {Object}
 */
const serializeGoal = (docSnap) => {
  const data = docSnap.data();
  return {
    id: docSnap.id,
    title: data.title || '',
    category: data.category || 'custom',
    targetValue: data.targetValue ?? null,
    currentValue: data.currentValue ?? null,
    unit: data.unit || '',
    deadline: data.deadline instanceof Timestamp
      ? data.deadline.toDate().toISOString()
      : data.deadline || null,
    status: data.status || 'active',
    createdAt: data.createdAt instanceof Timestamp
      ? data.createdAt.toDate().toISOString()
      : data.createdAt || new Date().toISOString(),
    completedAt: data.completedAt instanceof Timestamp
      ? data.completedAt.toDate().toISOString()
      : data.completedAt || null,
  };
};

/**
 * Create a new fitness goal.
 * @param {string} userId - The user's ID.
 * @param {Object} goalData - The goal data.
 * @param {string} goalData.title - Goal title.
 * @param {string} goalData.category - Category: 'weight' | 'strength' | 'cardio' | 'nutrition' | 'habit' | 'custom'.
 * @param {number|null} [goalData.targetValue] - Target value for measurable goals.
 * @param {string} [goalData.unit] - Unit of measurement.
 * @param {Date|null} [goalData.deadline] - Optional deadline.
 * @returns {Promise<Object>} The created goal object.
 */
export const createGoal = async (userId, goalData) => {
  try {
    const goalsRef = collection(db, USERS_COLLECTION, userId, GOALS_SUBCOLLECTION);
    const goalRef = doc(goalsRef);
    const goalId = goalRef.id;

    const data = {
      title: goalData.title || '',
      category: goalData.category || 'custom',
      targetValue: goalData.targetValue ?? null,
      currentValue: goalData.currentValue ?? 0,
      unit: goalData.unit || '',
      deadline: goalData.deadline ? Timestamp.fromDate(new Date(goalData.deadline)) : null,
      status: 'active',
      createdAt: serverTimestamp(),
      completedAt: null,
    };

    await setDoc(goalRef, data);

    return {
      id: goalId,
      ...data,
      deadline: goalData.deadline || null,
      createdAt: new Date().toISOString(),
    };
  } catch (error) {
    handleServiceError(error, 'Create goal', 'Failed to create goal. Please try again.');
  }
};

/**
 * Get goals for a user filtered by status.
 * @param {string} userId - The user's ID.
 * @param {string} [status='active'] - Goal status filter.
 * @returns {Promise<Array>} Array of goal objects.
 */
export const getGoals = async (userId, status = 'active') => {
  try {
    const goalsRef = collection(db, USERS_COLLECTION, userId, GOALS_SUBCOLLECTION);
    const q = query(
      goalsRef,
      where('status', '==', status),
      orderBy('createdAt', 'desc')
    );

    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(serializeGoal);
  } catch (error) {
    handleServiceError(error, 'Get goals', 'Failed to load goals. Please try again.');
  }
};

/**
 * Update the progress value of a goal.
 * @param {string} userId - The user's ID.
 * @param {string} goalId - The goal document ID.
 * @param {number} currentValue - The new progress value.
 * @returns {Promise<Object>} The updated goal object.
 */
export const updateGoalProgress = async (userId, goalId, currentValue) => {
  try {
    const goalRef = doc(db, USERS_COLLECTION, userId, GOALS_SUBCOLLECTION, goalId);
    await updateDoc(goalRef, { currentValue });

    const updatedSnap = await getDoc(goalRef);
    return serializeGoal(updatedSnap);
  } catch (error) {
    handleServiceError(error, 'Update goal progress', 'Failed to update goal progress. Please try again.');
  }
};

/**
 * Mark a goal as completed.
 * @param {string} userId - The user's ID.
 * @param {string} goalId - The goal document ID.
 * @returns {Promise<Object>} The updated goal object.
 */
export const completeGoal = async (userId, goalId) => {
  try {
    const goalRef = doc(db, USERS_COLLECTION, userId, GOALS_SUBCOLLECTION, goalId);
    await updateDoc(goalRef, {
      status: 'completed',
      completedAt: serverTimestamp(),
    });

    const updatedSnap = await getDoc(goalRef);
    return serializeGoal(updatedSnap);
  } catch (error) {
    handleServiceError(error, 'Complete goal', 'Failed to complete goal. Please try again.');
  }
};

/**
 * Mark a goal as abandoned.
 * @param {string} userId - The user's ID.
 * @param {string} goalId - The goal document ID.
 * @returns {Promise<Object>} The updated goal object.
 */
export const abandonGoal = async (userId, goalId) => {
  try {
    const goalRef = doc(db, USERS_COLLECTION, userId, GOALS_SUBCOLLECTION, goalId);
    await updateDoc(goalRef, { status: 'abandoned' });

    const updatedSnap = await getDoc(goalRef);
    return serializeGoal(updatedSnap);
  } catch (error) {
    handleServiceError(error, 'Abandon goal', 'Failed to abandon goal. Please try again.');
  }
};

/**
 * Delete a goal permanently.
 * @param {string} userId - The user's ID.
 * @param {string} goalId - The goal document ID.
 * @returns {Promise<string>} The deleted goal ID.
 */
export const deleteGoal = async (userId, goalId) => {
  try {
    const goalRef = doc(db, USERS_COLLECTION, userId, GOALS_SUBCOLLECTION, goalId);
    await deleteDoc(goalRef);
    return goalId;
  } catch (error) {
    handleServiceError(error, 'Delete goal', 'Failed to delete goal. Please try again.');
  }
};
